/**
 * Printing the ticket the customer is holding.
 *
 * The paper comes out of a printer attached to this machine, and a browser may not
 * open a USB device — so a small program, qms-agent, runs on the kiosk and listens
 * on the loopback interface. This module is the page's half of that: at the kiosk
 * one POST, fire and forget; at reception, a question first — is there paper
 * here? — and a print whose failure the greeter is told about.
 *
 * Two rules, and they are the same rule from two directions.
 *
 * Nothing here is awaited before the number is shown. The ticket exists the moment
 * the server says so, and a printer that is jammed, switched off, or not yet
 * delivered must never be able to leave a customer with no place in the queue.
 *
 * And nothing here is ever shown to the customer. A failure is the machine's
 * problem, reported by the agent's own health endpoint to whoever maintains it; a
 * customer standing at a kiosk can do nothing with the news that a device they
 * cannot see did not answer. Reception is the exception that proves it: there a
 * member of staff is at the screen and can act, so printTicketAndSay tells them.
 *
 * The address is http://127.0.0.1 from an https page, which browsers permit
 * because the loopback interface is treated as a trustworthy origin. That is worth
 * knowing before somebody tries to "fix" it with a certificate. Whether a current
 * Chrome also wants a local-network permission, or a private-network answer to a
 * preflight, for a public page calling loopback has not been checked on a real
 * machine yet; the agent sends no such answer.
 */

import type { PrinterSettings } from './types'
import type { LayoutDocument } from '../layout/types'

/** Where the agent listens. Overridable per machine, for an installation that had
 *  to move it off the default port. */
const agentAddress = () => {
  try {
    return window.localStorage.getItem('qms.agent') ?? 'http://127.0.0.1:9110'
  } catch {
    // A locked-down kiosk browser can refuse local storage entirely. The default
    // is the right answer on every machine that has not been told otherwise.
    return 'http://127.0.0.1:9110'
  }
}

export interface PrintableTicket {
  number: string
  branch?: string
  service?: string
  ahead?: number
  /** Whether ahead was counted at all. The agent reads a missing ahead as zero,
   *  so without this "nobody in front" and "the server could not count" would
   *  print the same — "you are next", to a customer it knows nothing about. */
  aheadKnown?: boolean
  waitMin?: number
  /** The top of a range for a long wait: "waitMin–waitMaxMin min". */
  waitMaxMin?: number
  /** The wait runs past the branch's closing time: "May not be served today". */
  late?: boolean
  issuedAt?: string
  /** The branch's timezone, which is the clock the time on the slip is read on.
   *  Sent rather than left to the machine: a mini-PC imaged in one place and
   *  shipped to ten branches carries one zone, and it is the wrong one in nine. */
  timezone?: string
  language?: string
  /** How this machine's slip should look, from its unit row. Anything left unset
   *  falls back to the agent's own flags, so a machine installed before any of
   *  this existed goes on printing exactly as it did. */
  printer?: PrinterSettings
  /**
   * The printed-ticket design published for this machine, if one is.
   *
   * Sent with every ticket rather than configured on the machine, for the same
   * reason the paper width is: changing how a branch's slip looks should be a
   * screen at head office, not a visit to ten buildings. The agent draws it, and
   * falls back to its built-in slip if it cannot — so a design that is wrong
   * costs a plainer ticket, never a customer with no ticket.
   */
  design?: LayoutDocument
  /**
   * The pictures the design refers to, by asset identifier, base64 encoded.
   *
   * Carried rather than fetched. The agent talks to nothing but a printer and a
   * port — that is what lets whoever plugs the machine in install it — so the page,
   * which already has a connection to the server, brings the bytes to it. A logo is
   * a few kilobytes and this is a loopback socket.
   */
  pictures?: Record<string, string>
  /**
   * The values this ticket carries, keyed the way a design's "{{key}}" names them.
   *
   * Server-filtered by exactly what a ticket design's own surface
   * (surfaceOf(KindTicket), the same one a kiosk's own screen is filtered
   * against) currently permits — never the ticket's own unfiltered `fields`,
   * which the screen route deliberately does not carry back to this page at
   * all. Save-time validation already refuses a design that names a key the
   * declaration does not permit, but a field can be retired or moved off a
   * surface after a design was published that still references it; this is
   * what keeps that from printing anyway. Empty for a kiosk-issued ticket
   * today: nothing collects a value at a kiosk yet. Carried anyway, so a
   * design with a placeholder in it prints nothing rather than the literal
   * "{{key}}" the moment a value does exist to send.
   */
  fields?: Record<string, string>
  /** The link the slip's code carries, minted by the server for this ticket.
   *  Absent prints the number in the code, as every slip did before it. */
  followUrl?: string
  /** "Valid until" on the slip: the moment the server computed at issue, RFC 3339 in
   *  the branch's offset. Absent prints nothing in that element. */
  validUntil?: string
}

/**
 * One request to this machine's agent — a GET for a question, a POST for a
 * ticket — and the reading of its answer, both inside one timeout.
 *
 * The timeout is short because the printer is in the same room: anything slower
 * than a second and a half is a fault, and waiting longer would only hold the page
 * open. It bounds the reading of the body as well as the arrival of the headers:
 * an agent that answers the headers and then stalls is as much a fault as one
 * that never answers, and a timer cleared when the headers came would leave the
 * page waiting on it for ever. Aborting mid-body makes the read reject, which
 * every caller already treats as no answer.
 *
 * A GET carries no headers of its own, so a browser sends it without asking the
 * agent first.
 */
async function send<T>(path: string, ticket: PrintableTicket | undefined, read: (res: Response) => Promise<T> | T): Promise<T> {
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => controller.abort(), AGENT_TIMEOUT_MS)
  const init: RequestInit =
    ticket === undefined
      ? { method: 'GET', signal: controller.signal }
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ticket),
          signal: controller.signal,
        }
  try {
    const res = await fetch(`${agentAddress()}${path}`, init)
    return await read(res)
  } finally {
    globalThis.clearTimeout(timer)
  }
}

/** How long a request to the agent, answer included, may take. */
export const AGENT_TIMEOUT_MS = 1500

/**
 * Send one ticket to this machine's printer.
 *
 * Never throws and never returns anything the caller has to act on. This is the
 * kiosk's: nothing is awaited before the number is shown, and the customer is never
 * told about a printer they cannot see. A refusal or no answer goes to report,
 * which the kiosk sends to the server's log; what the printer now is goes to
 * state, for the Machines page. A slow answer changes no state: it may still print.
 */
export function printTicket(
  ticket: PrintableTicket,
  report: (message: string) => void,
  state?: (state: PrinterState, error?: string) => void,
): void {
  const say = (message: string) => {
    try {
      report(message)
    } catch {
      // A report that fails cannot be reported.
    }
  }
  const become = (next: PrinterState, error?: string) => {
    try {
      state?.(next, error)
    } catch {
      // As above.
    }
  }
  void send('/print', ticket, async (res) => {
    if (res.ok) {
      become('ready')
      return
    }
    become('faulty', `agent answered ${res.status}`)
    const said = await res.text().catch(() => '')
    say(`printer: agent answered ${res.status} for ${ticket.number}: ${said.trim().slice(0, 200)}`)
  }).catch((err: unknown) => {
    if (err instanceof Error && err.name === 'AbortError') {
      say(`printer: no answer within ${AGENT_TIMEOUT_MS / 1000} s for ${ticket.number} (may still print)`)
      return
    }
    become('absent', 'no agent answered')
    // No agent at all repeats on every ticket; the first, then one a minute.
    const now = Date.now()
    if (now - noAgent.last < NO_AGENT_EVERY_MS) {
      noAgent.held++
      return
    }
    const held = noAgent.held
    noAgent.last = now
    noAgent.held = 0
    say(`printer: no agent answered for ${ticket.number} (${held} unlogged before it): ${err instanceof Error ? err.message : String(err)}`)
  })
}

const NO_AGENT_EVERY_MS = 60_000
const noAgent = { last: -Infinity, held: 0 }

/** Forget the no-agent warning's last line; for tests. */
export function resetNoAgentWarning(): void {
  noAgent.last = -Infinity
  noAgent.held = 0
}

/** What a page may assume about this machine's paper. */
export type PrinterState = 'ready' | 'faulty' | 'absent'

/**
 * The agent's health answer, read strictly. Anything but an agent saying it prints
 * is no printer — an older agent that cannot be read, and one running
 * -printer none, included — because a greeter shown a Print button promises the
 * customer a slip.
 */
export function printerStateFrom(answer: { status: number; body: unknown } | null): PrinterState {
  if (!answer || typeof answer.body !== 'object' || answer.body === null) return 'absent'
  const body = answer.body as { status?: unknown; prints?: unknown }
  if (body.prints !== true) return 'absent'
  return answer.status === 200 && body.status === 'ok' ? 'ready' : 'faulty'
}

/** Ask this machine's agent whether there is paper here. Never throws. */
export async function probePrinter(): Promise<PrinterState> {
  try {
    return await send('/healthz', undefined, async (res) =>
      printerStateFrom({ status: res.status, body: await res.json().catch(() => null) }),
    )
  } catch {
    // Nothing listening, or an agent whose answer the browser would not hand to
    // this page (an older one, without CORS on its health), are the same thing
    // here: no printer the greeter can promise.
    return 'absent'
  }
}

/**
 * Print, and say whether it worked — for a screen with a member of staff at it,
 * who can act on a failure. The kiosk does not use this: see printTicket.
 */
export async function printTicketAndSay(ticket: PrintableTicket): Promise<boolean> {
  try {
    return await send('/print', ticket, (res) => res.ok)
  } catch {
    return false
  }
}
